import type { ProcessMachineNode } from '../types/processMachineMap';

export const MACHINE_PREVIEW_LIMIT = 40;

export type LayoutNode = {
  id: string;
  node: ProcessMachineNode;
  depth: number;
  x: number;
  y: number;
  width: number;
  height: number;
  parentId: string | null;
  childCount: number;
  hiddenChildren: number;
  truncated: boolean;
};

export type LayoutEdge = {
  id: string;
  fromId: string;
  toId: string;
};

const NODE_W: Record<string, number> = {
  fab: 236,
  process: 212,
  subprocess: 200,
  tool_group: 188,
  machine: 164,
};

const NODE_H = 60;
/** Vertical gap between parent and child levels (top → bottom). */
const LEVEL_GAP = 48;
/** Horizontal gap between sibling subtrees. */
const SIBLING_GAP = 16;

function visibleChildren(
  node: ProcessMachineNode,
  expanded: Set<string>
): { kids: ProcessMachineNode[]; hidden: number; truncated: boolean } {
  const all = node.children ?? [];
  if (!expanded.has(node.id) || all.length === 0) {
    return { kids: [], hidden: all.length, truncated: false };
  }

  if (node.type === 'tool_group' && all.length > MACHINE_PREVIEW_LIMIT) {
    return {
      kids: all.slice(0, MACHINE_PREVIEW_LIMIT),
      hidden: all.length - MACHINE_PREVIEW_LIMIT,
      truncated: true,
    };
  }

  return { kids: all, hidden: 0, truncated: false };
}

function nodeWidth(node: ProcessMachineNode): number {
  return NODE_W[node.type] ?? 160;
}

/** Measure subtree width for tidy horizontal packing (top → bottom layout). */
function measureWidth(node: ProcessMachineNode, expanded: Set<string>): number {
  const { kids } = visibleChildren(node, expanded);
  const selfW = nodeWidth(node);
  if (kids.length === 0) return selfW;
  let sum = 0;
  for (let i = 0; i < kids.length; i++) {
    if (i > 0) sum += SIBLING_GAP;
    sum += measureWidth(kids[i]!, expanded);
  }
  return Math.max(selfW, sum);
}

function place(
  node: ProcessMachineNode,
  depth: number,
  left: number,
  y: number,
  parentId: string | null,
  expanded: Set<string>,
  outNodes: LayoutNode[],
  outEdges: LayoutEdge[]
): number {
  const subtreeW = measureWidth(node, expanded);
  const width = nodeWidth(node);
  const x = left + subtreeW / 2 - width / 2;
  const { kids, hidden, truncated } = visibleChildren(node, expanded);

  outNodes.push({
    id: node.id,
    node,
    depth,
    x,
    y,
    width,
    height: NODE_H,
    parentId,
    childCount: node.children?.length ?? 0,
    hiddenChildren: hidden,
    truncated,
  });

  if (parentId) {
    outEdges.push({ id: `${parentId}->${node.id}`, fromId: parentId, toId: node.id });
  }

  let childLeft = left;
  const childY = y + NODE_H + LEVEL_GAP;
  for (let i = 0; i < kids.length; i++) {
    const child = kids[i]!;
    const childW = measureWidth(child, expanded);
    place(child, depth + 1, childLeft, childY, node.id, expanded, outNodes, outEdges);
    childLeft += childW + SIBLING_GAP;
  }

  return subtreeW;
}

export function layoutMindmapTree(
  root: ProcessMachineNode,
  expanded: Set<string>
): { nodes: LayoutNode[]; edges: LayoutEdge[]; width: number; height: number } {
  const nodes: LayoutNode[] = [];
  const edges: LayoutEdge[] = [];
  const width = place(root, 0, 24, 24, null, expanded, nodes, edges);
  const height = nodes.reduce((max, n) => Math.max(max, n.y + n.height), 0) + 48;
  return { nodes, edges, width: width + 48, height };
}

export function collectExpandableIds(root: ProcessMachineNode): string[] {
  const ids: string[] = [];
  const walk = (n: ProcessMachineNode) => {
    if (n.children && n.children.length > 0) ids.push(n.id);
    n.children?.forEach(walk);
  };
  walk(root);
  return ids;
}

export function defaultExpandedIds(root: ProcessMachineNode): Set<string> {
  // One level only: expand the root so its children are visible, but keep those collapsed.
  return new Set<string>([root.id]);
}

export function findNodeById(
  root: ProcessMachineNode,
  id: string
): ProcessMachineNode | null {
  if (root.id === id) return root;
  for (const child of root.children ?? []) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
}

export type MindmapTreeFilters = {
  query?: string;
  /** Empty / omitted = all types. */
  types?: ReadonlySet<string> | readonly string[];
  /** Exact work_cell match (usually on machines). */
  workCell?: string | null;
  /** Keep only machines that have map coordinates (ancestors kept for path). */
  onMapOnly?: boolean;
};

function normalizeTypeSet(
  types?: ReadonlySet<string> | readonly string[]
): Set<string> | null {
  if (!types) return null;
  const set = types instanceof Set ? types : new Set(types);
  return set.size > 0 ? set : null;
}

export function hasMindmapFilters(filters: MindmapTreeFilters): boolean {
  const q = filters.query?.trim();
  const types = normalizeTypeSet(filters.types);
  return Boolean(q || types || filters.workCell || filters.onMapOnly);
}

/** Unique work-cell values under the tree (sorted). */
export function collectWorkCells(root: ProcessMachineNode): string[] {
  const cells = new Set<string>();
  const walk = (n: ProcessMachineNode) => {
    if (n.work_cell?.trim()) cells.add(n.work_cell.trim());
    n.children?.forEach(walk);
  };
  walk(root);
  return Array.from(cells).sort((a, b) => a.localeCompare(b));
}

export function filterTree(
  root: ProcessMachineNode,
  queryOrFilters: string | MindmapTreeFilters = ''
): ProcessMachineNode | null {
  const filters: MindmapTreeFilters =
    typeof queryOrFilters === 'string' ? { query: queryOrFilters } : queryOrFilters;

  const q = filters.query?.trim().toLowerCase() ?? '';
  const types = normalizeTypeSet(filters.types);
  const workCell = filters.workCell?.trim() || null;
  const onMapOnly = Boolean(filters.onMapOnly);

  if (!q && !types && !workCell && !onMapOnly) return root;

  const matchSelf = (n: ProcessMachineNode) => {
    if (types && !types.has(n.type)) return false;

    if (workCell) {
      // Work cell is primarily a machine field; non-machines pass through via children.
      if (n.type === 'machine' || n.work_cell) {
        if ((n.work_cell ?? '').trim() !== workCell) return false;
      } else {
        return false;
      }
    }

    if (onMapOnly) {
      const loc = n.location;
      const hasLoc =
        Array.isArray(loc) &&
        loc.length >= 2 &&
        Number.isFinite(loc[0]) &&
        Number.isFinite(loc[1]);
      if (!hasLoc) return false;
    }

    if (q) {
      const hay = [
        n.name,
        n.code,
        n.type,
        n.tool_name,
        n.tool_number,
        n.equipment_type,
        n.work_cell,
        n.layer,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }

    return true;
  };

  const walk = (n: ProcessMachineNode): ProcessMachineNode | null => {
    if (matchSelf(n)) {
      return n;
    }

    const filteredChildren = (n.children ?? [])
      .map(walk)
      .filter((c): c is ProcessMachineNode => !!c);

    if (filteredChildren.length === 0) return null;

    return {
      ...n,
      children: filteredChildren,
      count: countMachines(filteredChildren),
    };
  };

  return walk(root);
}

function countMachines(nodes: ProcessMachineNode[]): number {
  let total = 0;
  for (const n of nodes) {
    if (n.type === 'machine') total += 1;
    else total += countMachines(n.children ?? []);
  }
  return total;
}

export function nodeTypeLabel(type: ProcessMachineNode['type'] | string): string {
  switch (type) {
    case 'fab':
      return 'Fab';
    case 'process':
      return 'Process';
    case 'subprocess':
      return 'Subprocess';
    case 'tool_group':
      return 'Tool group';
    case 'machine':
      return 'Machine';
    default:
      return type;
  }
}

/** Selected node id plus every descendant id. */
export function collectSubtreeIds(node: ProcessMachineNode): string[] {
  const ids: string[] = [node.id];
  const walk = (n: ProcessMachineNode) => {
    for (const child of n.children ?? []) {
      ids.push(child.id);
      walk(child);
    }
  };
  walk(node);
  return ids;
}

export type MachineMarkerSource = {
  id: string;
  name: string;
  locX: number;
  locY: number;
};

/** Collect machine leaves under a node that have map coordinates. */
export function collectMachineMarkers(node: ProcessMachineNode): {
  markers: MachineMarkerSource[];
  machineCount: number;
} {
  let machineCount = 0;
  const markers: MachineMarkerSource[] = [];

  const walk = (n: ProcessMachineNode) => {
    if (n.type === 'machine') {
      machineCount += 1;
      if (
        Array.isArray(n.location) &&
        n.location.length >= 2 &&
        Number.isFinite(n.location[0]) &&
        Number.isFinite(n.location[1])
      ) {
        markers.push({
          id: n.id,
          name: n.name,
          locX: n.location[0]!,
          locY: n.location[1]!,
        });
      }
      return;
    }
    for (const child of n.children ?? []) walk(child);
  };

  walk(node);
  return { markers, machineCount };
}

export function computeLocationBounds(
  root: ProcessMachineNode
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = false;

  const walk = (n: ProcessMachineNode) => {
    if (
      n.type === 'machine' &&
      Array.isArray(n.location) &&
      n.location.length >= 2 &&
      Number.isFinite(n.location[0]) &&
      Number.isFinite(n.location[1])
    ) {
      found = true;
      minX = Math.min(minX, n.location[0]!);
      minY = Math.min(minY, n.location[1]!);
      maxX = Math.max(maxX, n.location[0]!);
      maxY = Math.max(maxY, n.location[1]!);
    }
    for (const child of n.children ?? []) walk(child);
  };

  walk(root);
  if (!found) return null;
  return { minX, minY, maxX, maxY };
}

/** Map process-machine location space into floor-plan canvas bounds. */
export function locationToCanvasPoint(
  locX: number,
  locY: number,
  locationBounds: { minX: number; minY: number; maxX: number; maxY: number },
  floorBounds: { minX: number; minY: number; maxX: number; maxY: number }
): { x: number; y: number } {
  const lw = Math.max(1e-6, locationBounds.maxX - locationBounds.minX);
  const lh = Math.max(1e-6, locationBounds.maxY - locationBounds.minY);
  const fw = Math.max(1e-6, floorBounds.maxX - floorBounds.minX);
  const fh = Math.max(1e-6, floorBounds.maxY - floorBounds.minY);
  const nx = (locX - locationBounds.minX) / lw;
  const ny = (locY - locationBounds.minY) / lh;
  return {
    x: floorBounds.minX + nx * fw,
    y: floorBounds.minY + (1 - ny) * fh,
  };
}

/**
 * Project a process-machine map location into canvas space.
 *
 * The machine `location` is the INSERT's RESOLVED world bounding-box centre (`world_center`),
 * computed at import in RAW DXF units — the SAME coordinate frame as the floor-plan geometry and
 * the stored `transform` (both come from the raw lossless-JSON). So we apply the transform
 * DIRECTLY, exactly like the floor-plan polylines (`(raw − rawMin) × invScale`, with a Y-flip). We
 * must NOT scale by `dxfUnitsPerMeter` here — that would push markers far outside the plan.
 */
export function machineLocationToCanvas(
  locX: number,
  locY: number,
  transform: { rawMinX: number; rawMinY: number; invScale: number } | null | undefined,
  _dxfUnitsPerMeter?: number | null
): { x: number; y: number } | null {
  if (!transform || !(transform.invScale > 0)) return null;
  return {
    x: (locX - transform.rawMinX) * transform.invScale,
    y: (-locY - transform.rawMinY) * transform.invScale,
  };
}

function medianOf(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1]! + sorted[mid]!) / 2
    : sorted[mid]!;
}

/**
 * Drop source-location outliers (bad LLM/geo points) that would land far from
 * the selection's main cluster — e.g. one METR tool at (-1.8,-0.5) while peers
 * sit near (10, 10).
 */
export function filterLocationOutliers<T extends { locX: number; locY: number }>(
  markers: T[],
  maxDistanceMultiplier = 4
): { kept: T[]; dropped: T[] } {
  if (markers.length < 3) return { kept: markers, dropped: [] };

  const medX = medianOf(markers.map((m) => m.locX));
  const medY = medianOf(markers.map((m) => m.locY));
  const dists = markers.map((m) => Math.hypot(m.locX - medX, m.locY - medY));
  const medDist = medianOf(dists);
  // Allow a generous cluster radius; never use a cutoff smaller than 2 metres.
  const cutoff = Math.max(2, medDist * maxDistanceMultiplier, medianOf(dists.filter((d) => d > 0)) * 3);

  const kept: T[] = [];
  const dropped: T[] = [];
  for (let i = 0; i < markers.length; i++) {
    if (dists[i]! <= cutoff) kept.push(markers[i]!);
    else dropped.push(markers[i]!);
  }

  // If the filter would remove almost everything, keep original.
  if (kept.length < Math.max(2, Math.ceil(markers.length * 0.5))) {
    return { kept: markers, dropped: [] };
  }
  return { kept, dropped };
}

export function isInsideFloorBounds(
  x: number,
  y: number,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  paddingRatio = 0.02
): boolean {
  const padX = (bounds.maxX - bounds.minX) * paddingRatio;
  const padY = (bounds.maxY - bounds.minY) * paddingRatio;
  return (
    x >= bounds.minX - padX &&
    x <= bounds.maxX + padX &&
    y >= bounds.minY - padY &&
    y <= bounds.maxY + padY
  );
}

/** Compact footprint center used to snap mindmap markers onto drawn tools. */
export type MapFootprintAnchor = { x: number; y: number; radius: number };

/**
 * Collect circle-like / compact closed polylines (typical tool chambers) so
 * markers can snap from approximate backend locations onto visible geometry.
 */
export function collectFootprintAnchors(
  polylines: number[][],
  opts?: { minDiameter?: number; maxDiameter?: number }
): MapFootprintAnchor[] {
  const minD = opts?.minDiameter ?? 0;
  const maxD = opts?.maxDiameter ?? Infinity;
  const anchors: MapFootprintAnchor[] = [];

  for (const pl of polylines) {
    const n = Math.floor(pl.length / 2);
    if (n < 10) continue;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i + 1 < pl.length; i += 2) {
      const x = pl[i]!;
      const y = pl[i + 1]!;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    if (!Number.isFinite(minX)) continue;

    const w = maxX - minX;
    const h = maxY - minY;
    const diameter = Math.max(w, h);
    if (diameter < minD || diameter > maxD) continue;

    const aspect = w > 1e-9 && h > 1e-9 ? Math.max(w / h, h / w) : 99;
    // Chambers are roughly round; reject long corridors / walls.
    if (aspect > 1.45) continue;

    const x0 = pl[0]!;
    const y0 = pl[1]!;
    const xN = pl[(n - 1) * 2]!;
    const yN = pl[(n - 1) * 2 + 1]!;
    const closeDist = Math.hypot(xN - x0, yN - y0);
    const closed = closeDist <= diameter * 0.12;
    if (!closed && n < 20) continue;

    anchors.push({
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
      radius: diameter / 2,
    });
  }

  return anchors;
}

export function snapToNearestAnchor(
  x: number,
  y: number,
  anchors: MapFootprintAnchor[],
  maxDist: number
): { x: number; y: number; dist: number } | null {
  let best: { x: number; y: number; dist: number } | null = null;
  for (const a of anchors) {
    const dist = Math.hypot(a.x - x, a.y - y);
    if (dist > maxDist) continue;
    if (!best || dist < best.dist) best = { x: a.x, y: a.y, dist };
  }
  return best;
}

/**
 * Estimate a uniform canvas translation that aligns projected marker points to
 * nearby footprint anchors (median residual). Fixes small systematic CRS drift.
 */
export function estimateUniformMarkerOffset(
  points: Array<{ x: number; y: number }>,
  anchors: MapFootprintAnchor[],
  maxDist: number
): { dx: number; dy: number; samples: number } {
  if (points.length === 0 || anchors.length === 0) {
    return { dx: 0, dy: 0, samples: 0 };
  }

  const dxs: number[] = [];
  const dys: number[] = [];
  for (const p of points) {
    const hit = snapToNearestAnchor(p.x, p.y, anchors, maxDist);
    if (!hit) continue;
    dxs.push(hit.x - p.x);
    dys.push(hit.y - p.y);
  }

  if (dxs.length < Math.min(3, points.length)) {
    return { dx: 0, dy: 0, samples: dxs.length };
  }

  return { dx: medianOf(dxs), dy: medianOf(dys), samples: dxs.length };
}

/** Canvas units corresponding to one metre, when calibration is available. */
export function canvasUnitsPerMeterFromFloorPlan(params: {
  canvasUnitsPerMeter?: number | null;
  dxfUnitsPerMeter?: number | null;
  transform?: { invScale: number } | null;
}): number | null {
  if (
    Number.isFinite(params.canvasUnitsPerMeter) &&
    (params.canvasUnitsPerMeter as number) > 0
  ) {
    return params.canvasUnitsPerMeter as number;
  }
  const dxf =
    Number.isFinite(params.dxfUnitsPerMeter) && (params.dxfUnitsPerMeter as number) > 0
      ? (params.dxfUnitsPerMeter as number)
      : null;
  const inv = params.transform?.invScale;
  if (dxf && inv && inv > 0) return dxf * inv;
  return null;
}

/** Expand selected node (and parents) so its contents become visible. */
export function expandPathToNode(
  root: ProcessMachineNode,
  targetId: string,
  current: Set<string>
): Set<string> {
  const path: string[] = [];

  const find = (n: ProcessMachineNode, trail: string[]): boolean => {
    const nextTrail = [...trail, n.id];
    if (n.id === targetId) {
      path.push(...nextTrail);
      return true;
    }
    for (const child of n.children ?? []) {
      if (find(child, nextTrail)) return true;
    }
    return false;
  };

  find(root, []);
  const next = new Set(current);
  for (const id of path) next.add(id);
  return next;
}
